import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getInterviewDefaults, getInterviewConfiguration, createInterviewConfiguration,
  createCategory, previewBlueprint, listInterviewConfigurations, deleteInterviewConfiguration,
  getCompanyProfile, saveCompanyProfile, validateTopics,
  getCompanyPolicyStatus, listCompanyPolicyDocuments, addCompanyPolicyDocument, deleteCompanyPolicyDocument,
} from '../ai-interview/utils/aiInterviewApi';
import PriorityTopicsEditor from '../../components/interview/PriorityTopicsEditor';
import {
  MODES, FOLLOWUP_MODES, PLACEMENT_STRATEGIES, RECRUITER_ORDERS,
  getCompanyId, jobFromDefaults, companyFromDefaults, validateConfiguration,
  categorySum, recruiterCount, scoringSum, labelFor, normalizeWorkMode,
} from '../interview/config/interviewConfigurationModel';

/**
 * Interview Builder — design the interview BEFORE candidates join. Two tiers:
 *   • Per-job interview (session): the interview itself — mode, questions, category mix, timing.
 *   • Company policy (shared): scoring rubric, thresholds, follow-up depth — set ONCE, inherited.
 * Output is the Interview Blueprint the live interview runs on. Seeds from config.py defaults.
 * Proctoring/recording is the separate "Interview Flow" page.
 */

const inputCls = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900/40';
const chipBase = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium';

const Section = ({ title, desc, right, children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
        {desc && <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{desc}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

const Field = ({ label, hint, children }) => (
  <label className="block text-sm">
    <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-gray-400 dark:text-gray-500">{hint}</span>}
  </label>
);

const Pill = ({ ok, children }) => (
  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${ok
    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
    : 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400'}`}>{children}</span>
);

const cardBtn = (active) => `rounded-xl border p-3 text-left text-sm transition ${active
  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-300 dark:bg-primary-900/20 dark:ring-primary-700'
  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-gray-600 dark:hover:bg-gray-800'}`;

// merge a stored row over a seeded base (keeps the saved values, incl. a deliberately-empty mix).
const mapConfig = (c, base) => ({
  ...base,
  ...c,
  scoring_config: c.scoring_config && Object.keys(c.scoring_config).length ? c.scoring_config : base.scoring_config,
  threshold_config: c.threshold_config && Object.keys(c.threshold_config).length ? c.threshold_config : base.threshold_config,
  categories: c.categories || [],
  custom_questions: c.custom_questions || [],
});

const InterviewConfigurationPage = ({
  companyOnly = false,
  jobOnly = false,
  embedded = false,
  lockedSessionId = '',
  onSaved,
}) => {
  // Company id is auto-sent by the logged-in app in production; editable here so it can be forced while
  // testing (e.g. set to the candidate session's cid so the saved config matches).
  const [companyId, setCompanyId] = useState(getCompanyId());
  const [profile, setProfile] = useState({ name: '', address: '', description: '' });
  const [profileMsg, setProfileMsg] = useState(null);
  const [profileBusy, setProfileBusy] = useState(false);
  const [policyDocs, setPolicyDocs] = useState([]);
  const [policyStatus, setPolicyStatus] = useState(null);
  const [policyDraft, setPolicyDraft] = useState({ title: '', raw_text: '' });
  const [policyMsg, setPolicyMsg] = useState(null);
  const [policyBusy, setPolicyBusy] = useState(false);
  const [scope, setScope] = useState(companyOnly ? 'company' : 'job'); // 'job' | 'company'
  const [defaults, setDefaults] = useState(null);     // config.py base (from /defaults)
  const [catMeta, setCatMeta] = useState([]);         // enriched registry [{name,label,description,core,requires}]
  const [savedList, setSavedList] = useState([]);
  const [jobId, setJobId] = useState(lockedSessionId || '');
  const [selectedId, setSelectedId] = useState(null);
  const [cfg, setCfg] = useState(jobFromDefaults(null));
  const [aiMix, setAiMix] = useState('custom');       // 'auto' | 'custom' (AI mode only)
  const [preview, setPreview] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const previewTimer = useRef(null);

  const refreshList = useCallback(() => {
    if (companyOnly && (!companyId || companyId === '0')) {
      setSavedList([]);
      return;
    }
    listInterviewConfigurations(companyId)
      .then(({ configurations }) => setSavedList((configurations || []).filter((c) => c.session_id)))
      .catch(() => {});
  }, [companyId, companyOnly]);

  // bootstrap: real defaults (config.py) + saved interviews.
  useEffect(() => {
    if (companyOnly && (!companyId || companyId === '0')) {
      setReady(true);
      return;
    }
    getInterviewDefaults(companyId)
      .then(({ defaults: d, categories }) => { setDefaults(d || {}); setCatMeta(categories || []); })
      .catch(() => { setDefaults({}); setCatMeta([]); })
      .finally(() => setReady(true));
    refreshList();
  }, [companyId, companyOnly, refreshList]);

  const startNewJob = useCallback((d) => {
    setSelectedId(null); setJobId(lockedSessionId || ''); setMsg(null);
    const next = jobFromDefaults(d, lockedSessionId || '');
    setCfg(next);
    setAiMix((next.categories || []).length ? 'custom' : 'auto');
  }, [lockedSessionId]);

  const loadCompany = useCallback(async (d) => {
    setMsg(null);
    try {
      const { configuration } = await getInterviewConfiguration(companyId, '', true);
      setCfg(configuration ? mapConfig(configuration, companyFromDefaults(d)) : companyFromDefaults(d));
    } catch { setCfg(companyFromDefaults(d)); }
  }, [companyId]);

  // (re)initialise the form whenever defaults arrive, the tab switches, or the company id changes.
  useEffect(() => {
    if (!ready) return;
    if (companyOnly && (!companyId || companyId === '0')) return;
    if (scope === 'company') loadCompany(defaults);
    else startNewJob(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, scope, companyId, companyOnly]);

  useEffect(() => {
    if (companyOnly && scope !== 'company') setScope('company');
    if (jobOnly && scope !== 'job') setScope('job');
  }, [companyOnly, jobOnly, scope]);

  useEffect(() => {
    if (!ready || !jobOnly || !lockedSessionId) return;
    const existing = savedList.find((item) => String(item.session_id) === String(lockedSessionId));
    if (existing && Number(existing.id) !== Number(selectedId)) loadSaved(existing);
  // loadSaved intentionally runs only when the locked session list resolves.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, jobOnly, lockedSessionId, savedList]);

  // Load the company profile (name/address/description) when on the company tab.
  useEffect(() => {
    if (!ready || scope !== 'company') return;
    if (companyOnly && (!companyId || companyId === '0')) return;
    setProfileMsg(null);
    getCompanyProfile(companyId)
      .then(({ company_profile }) => company_profile && setProfile({
        name: company_profile.name || '', address: company_profile.address || '',
        description: company_profile.description || '',
      }))
      .catch(() => {});
  }, [ready, scope, companyId, companyOnly]);

  const saveProfile = async () => {
    setProfileBusy(true); setProfileMsg(null);
    try {
      await saveCompanyProfile(companyId, profile);
      setProfileMsg({ type: 'success', text: 'Company details saved.' });
    } catch (e) { setProfileMsg({ type: 'error', text: e.message || 'Could not save.' }); }
    finally { setProfileBusy(false); }
  };

  const refreshPolicyDocs = useCallback(async () => {
    if (!companyId || companyId === '0') return;
    try {
      const [statusData, docsData] = await Promise.all([
        getCompanyPolicyStatus(companyId),
        listCompanyPolicyDocuments(companyId),
      ]);
      setPolicyStatus(statusData?.policy || null);
      setPolicyDocs(docsData?.documents || []);
    } catch {
      setPolicyStatus(null);
      setPolicyDocs([]);
    }
  }, [companyId]);

  useEffect(() => {
    if (!ready || scope !== 'company') return;
    refreshPolicyDocs();
  }, [ready, scope, refreshPolicyDocs]);

  const savePolicyDocument = async () => {
    const raw = String(policyDraft.raw_text || '').trim();
    if (!raw) {
      setPolicyMsg({ type: 'error', text: 'Paste the policy text before indexing.' });
      return;
    }
    setPolicyBusy(true); setPolicyMsg(null);
    try {
      await addCompanyPolicyDocument(companyId, {
        title: policyDraft.title || 'Company policy',
        raw_text: raw,
        document_type: 'policy',
      });
      setPolicyDraft({ title: '', raw_text: '' });
      await refreshPolicyDocs();
      setPolicyMsg({ type: 'success', text: 'Policy document indexed for candidate Q&A.' });
    } catch (e) {
      setPolicyMsg({ type: 'error', text: e.message || 'Could not index policy document.' });
    } finally {
      setPolicyBusy(false);
    }
  };

  const removePolicyDocument = async (doc) => {
    if (!doc?.id) return;
    if (!window.confirm(`Delete policy document “${doc.title || doc.id}”?`)) return;
    setPolicyBusy(true); setPolicyMsg(null);
    try {
      await deleteCompanyPolicyDocument(companyId, doc.id);
      await refreshPolicyDocs();
      setPolicyMsg({ type: 'success', text: 'Policy document deleted.' });
    } catch (e) {
      setPolicyMsg({ type: 'error', text: e.message || 'Could not delete policy document.' });
    } finally {
      setPolicyBusy(false);
    }
  };

  // live blueprint preview (debounced). Backend merges inherited company policy under the draft.
  useEffect(() => {
    if (!ready) return undefined;
    if (companyOnly && (!companyId || companyId === '0')) return undefined;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try { setPreview((await previewBlueprint({ ...cfg, session_id: scope === 'job' ? jobId : '' }, companyId)).blueprint); }
      catch { setPreview(null); }
    }, 450);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [cfg, companyId, ready, scope, jobId, companyOnly]);

  const errors = useMemo(() => validateConfiguration({ ...cfg, session_id: scope === 'job' ? jobId : '' }, scope), [cfg, scope, jobId]);
  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const total = Number(cfg.total_questions) || 0;
  const isAI = cfg.mode === 'ai';
  const isRecruiter = cfg.mode === 'recruiter';
  const isHybrid = cfg.mode === 'hybrid';
  const aiTarget = isHybrid ? Math.max(0, total - recruiterCount(cfg)) : total;
  const catMatch = isAI ? (aiMix === 'auto' || categorySum(cfg) === total) : (categorySum(cfg) === aiTarget || (isHybrid && categorySum(cfg) === 0));

  const loadSaved = async (item, announce = false) => {
    setMsg(null); setSelectedId(item.id); setJobId(item.session_id);
    try {
      const { configuration } = await getInterviewConfiguration(companyId, item.session_id, true);
      const next = configuration ? mapConfig(configuration, jobFromDefaults(defaults, item.session_id)) : jobFromDefaults(defaults, item.session_id);
      setCfg(next);
      setAiMix((next.categories || []).length ? 'custom' : 'auto');
      if (announce) setMsg({ type: 'info', text: `This session already has a saved interview under Company ID ${companyId} — loaded “${item.name || item.session_id}” for editing (Update to overwrite it).` });
    } catch { setCfg(jobFromDefaults(defaults, item.session_id)); }
  };

  // Typing a session id that already has a config (under this Company ID) → load it for editing instead
  // of silently creating a duplicate. Prevents the "saved under the wrong cid" confusion.
  const maybeLoadExisting = () => {
    if (selectedId) return;
    const sid = jobId.trim();
    if (!sid) return;
    const match = (savedList || []).find((c) => c.session_id === sid);
    if (match) loadSaved(match, true);
  };

  const removeSaved = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the interview config for “${item.name || item.session_id}”?`)) return;
    await deleteInterviewConfiguration(item.id).catch(() => {});
    if (selectedId === item.id) startNewJob(defaults);
    refreshList();
  };

  // ── category editing ──
  const setAiMixMode = (m) => {
    setAiMix(m);
    if (m === 'auto') set({ categories: [] });
    else if (!(cfg.categories || []).length) set({ categories: ((defaults && defaults.category_distribution) || []).map((c) => ({ ...c })) });
  };
  const addCategory = () => {
    const used = new Set((cfg.categories || []).map((c) => c.category_name));
    const next = (catMeta.find((r) => !used.has(r.name)) || catMeta[0] || { name: 'technical' }).name;
    set({ categories: [...(cfg.categories || []), { category_name: next, question_count: 1 }] });
  };
  const setCategory = (i, patch) => set({ categories: cfg.categories.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const bumpCategory = (i, delta) => setCategory(i, { question_count: Math.max(0, (Number(cfg.categories[i].question_count) || 0) + delta) });
  const removeCategory = (i) => set({ categories: cfg.categories.filter((_, j) => j !== i) });
  const addNewCategoryType = async () => {
    const name = (window.prompt('New category name (e.g. system_design):') || '').trim();
    if (!name) return;
    const description = (window.prompt('Short description (optional):') || '').trim();
    await createCategory(companyId, name, description).catch(() => {});
    const { categories } = await getInterviewDefaults(companyId).catch(() => ({ categories: catMeta }));
    setCatMeta(categories || []);
  };

  // ── recruiter questions ──
  const addQuestion = () => set({
    custom_questions: [...(cfg.custom_questions || []), {
      question_text: '', category: (catMeta[0] || { name: 'technical' }).name,
      expected_skills: [], weightage: 1, sequence_order: (cfg.custom_questions || []).length,
    }],
  });
  const setQuestion = (i, patch) => set({ custom_questions: cfg.custom_questions.map((q, j) => (j === i ? { ...q, ...patch } : q)) });
  const removeQuestion = (i) => set({ custom_questions: cfg.custom_questions.filter((_, j) => j !== i).map((q, j) => ({ ...q, sequence_order: j })) });
  // Reorder a question (the AI asks them in this order) — swap with its neighbour, then renumber.
  const moveQuestion = (i, dir) => {
    const list = [...(cfg.custom_questions || [])];
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    set({ custom_questions: list.map((q, k) => ({ ...q, sequence_order: k })) });
  };

  // ── scoring (company tier) ──
  const setCriterion = (i, patch) => set({
    scoring_config: { ...cfg.scoring_config, criteria: (cfg.scoring_config.criteria || []).map((c, j) => (j === i ? { ...c, ...patch } : c)) },
  });

  const save = async () => {
    if (companyOnly && (!companyId || companyId === '0')) {
      setMsg({ type: 'error', text: 'Company context is unavailable. Please sign in again.' });
      return;
    }
    const errs = validateConfiguration({ ...cfg, session_id: scope === 'job' ? jobId.trim() : '' }, scope);
    if (errs.length) { setMsg({ type: 'error', text: errs[0] }); return; }
    setBusy(true);
    try {
      const payload = scope === 'company'
        ? { ...cfg, session_id: '', mode: cfg.mode || 'ai' }
        : { ...cfg, session_id: jobId.trim(), interview_id: (cfg.interview_id || jobId).trim(),
            scoring_config: cfg.scoring_config || {}, threshold_config: cfg.threshold_config || {} };
      const result = await createInterviewConfiguration(payload, companyId);
      setMsg({ type: 'success', text: scope === 'company' ? 'Company policy saved.' : `Saved interview “${cfg.name || jobId}”.` });
      if (scope === 'job') refreshList();
      if (scope === 'job' && onSaved) onSaved(result?.configuration || { ...payload });
    } catch (e) { setMsg({ type: 'error', text: e.message || 'Could not save.' }); }
    finally { setBusy(false); }
  };

  const catBadges = (name) => {
    const m = catMeta.find((c) => c.name === name);
    if (!m) return null;
    return (
      <>
        {m.core && <span className={`${chipBase} bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400`}>core</span>}
        {m.requires === 'resume' && <span className={`${chipBase} bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400`}>needs resume</span>}
        {m.requires === 'jd' && <span className={`${chipBase} bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400`}>needs JD</span>}
        {!m.is_builtin && <span className={`${chipBase} bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400`}>custom</span>}
      </>
    );
  };

  const modeMeta = MODES.find((m) => m.value === cfg.mode) || {};

  if (companyOnly && (!companyId || companyId === '0')) {
    return (
      <div className="px-6 py-8">
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
          <h1 className="text-lg font-semibold">Company context unavailable</h1>
          <p className="mt-1 text-sm">Please sign out and sign in again before changing AI interview settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? 'py-2' : 'px-6 py-8'}>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className={`${embedded ? 'text-lg' : 'text-2xl'} font-bold text-gray-900 dark:text-white`}>{companyOnly ? 'AI Interview Settings' : 'Interview Builder'}</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {companyOnly ? 'Set the shared company policy inherited by AI interviews.' : 'Design what the AI asks and how it scores — before candidates join.'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {!companyOnly && !jobOnly && <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span className="whitespace-nowrap">Company ID</span>
            <input value={companyId} onChange={(e) => setCompanyId(e.target.value.trim())}
              title="Auto-set by the app in production; editable here for testing."
              className="w-20 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
          </label>}
          {!companyOnly && !jobOnly && <button type="button" onClick={() => setShowHelp((s) => !s)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800">
            {showHelp ? 'Hide guide' : 'How it works'}
          </button>}
        </div>
      </div>

      {!companyOnly && !jobOnly && showHelp && (
        <div className="mb-6 rounded-2xl border border-primary-100 bg-primary-50/60 p-5 text-sm text-gray-700 dark:border-primary-900/40 dark:bg-primary-900/10 dark:text-gray-300">
          <p className="mb-2"><strong>Two things to set up:</strong></p>
          <ul className="ml-4 list-disc space-y-1.5">
            <li><strong>Per-job interview</strong> — one per posting. Choose a <em>mode</em> (AI / your questions / hybrid),
              the <em>category mix</em>, total questions and duration. Saved against that posting’s Session/Job ID.</li>
            <li><strong>Company policy</strong> — your <em>scoring rubric</em>, selection <em>thresholds</em> and
              <em> follow-up depth</em>. Set once; every job inherits it.</li>
          </ul>
          <p className="mt-3">“Core” categories (intro, technical, problem-solving, behavioural) are always asked.
            Resume/JD categories run only when the candidate has a resume / the posting has a JD. Saving creates or
            updates one row per scope; the next interview for that job uses it automatically.</p>
        </div>
      )}

      {/* scope tabs */}
      {!companyOnly && !jobOnly && (
      <div className="mb-6 inline-flex rounded-xl border border-gray-200 bg-gray-100 p-1 dark:border-gray-700 dark:bg-gray-800">
        {[['job', 'Per-job interview'], ['company', 'Company policy (shared)']].map(([v, label]) => (
          <button key={v} type="button" onClick={() => setScope(v)}
            className={`rounded-lg px-4 py-1.5 text-sm font-medium transition ${scope === v
              ? 'bg-primary-600 text-white shadow'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}>
            {label}
          </button>
        ))}
      </div>
      )}

      {!ready ? <div className="rounded-2xl border border-gray-200 bg-white p-8 text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400">Loading defaults…</div> : (
        <div className="grid items-start gap-6 lg:grid-cols-3">
          {/* ════════ LEFT: form ════════ */}
          <div className="space-y-5 lg:col-span-2">
            {scope === 'job' && (
              <>
                {!jobOnly && <Section title="Your interviews"
                  desc="Pick one to edit, or start a new one. Each is tied to a posting’s Session/Job ID."
                  right={<button type="button" onClick={() => startNewJob(defaults)}
                    className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-primary-700">+ New</button>}>
                  {savedList.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500">No saved interviews yet — fill in the form and save your first.</p>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {savedList.map((it) => (
                        <button key={it.id} type="button" onClick={() => loadSaved(it)}
                          className={`flex items-center justify-between rounded-xl border p-3 text-left transition ${selectedId === it.id
                            ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-200 dark:bg-primary-900/20 dark:ring-primary-800'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-gray-600 dark:hover:bg-gray-800'}`}>
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-gray-800 dark:text-gray-100">{it.name || it.session_id}</div>
                            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                              <span className="capitalize">{it.mode}</span> · {it.total_questions} Qs · {it.duration_minutes} min
                              <span className="ml-1 text-gray-400 dark:text-gray-500">· {it.session_id}</span>
                            </div>
                          </div>
                          <span role="button" tabIndex={0} onClick={(e) => removeSaved(e, it)}
                            onKeyDown={(e) => e.key === 'Enter' && removeSaved(e, it)}
                            className="ml-2 shrink-0 rounded px-2 text-gray-300 hover:text-rose-500 dark:text-gray-600">✕</span>
                        </button>
                      ))}
                    </div>
                  )}
                </Section>}

                <Section title={selectedId ? 'Edit interview' : 'New interview'}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Interview name">
                      <input className={inputCls} value={cfg.name || ''} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Java Backend — Round 1" />
                    </Field>
                    {jobOnly ? (
                      <Field label="Job / Session">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">{jobId}</div>
                      </Field>
                    ) : (
                      <Field label="Job / Session ID" hint="The candidate session this interview runs for.">
                        <input className={inputCls} value={jobId} onChange={(e) => setJobId(e.target.value)} onBlur={maybeLoadExisting} placeholder="e.g. session_abc123" disabled={!!selectedId} />
                      </Field>
                    )}
                    <Field label="Role">
                      <input className={inputCls} value={cfg.setup_details?.role_title || ''} onChange={(e) => set({ setup_details: { ...(cfg.setup_details || {}), role_title: e.target.value } })} placeholder="e.g. Area Sales Manager" />
                    </Field>
                    <Field label="Role level">
                      <select className={inputCls} value={cfg.setup_details?.role_level || ''} onChange={(e) => set({ setup_details: { ...(cfg.setup_details || {}), role_level: e.target.value } })}>
                        <option value="">Not set</option>
                        {['entry', 'junior', 'mid', 'senior', 'lead', 'principal', 'manager', 'head'].map((level) => <option key={level} value={level}>{level}</option>)}
                      </select>
                    </Field>
                    <Field label="Department">
                      <input className={inputCls} value={cfg.setup_details?.department || ''} onChange={(e) => set({ setup_details: { ...(cfg.setup_details || {}), department: e.target.value } })} placeholder="e.g. Sales" />
                    </Field>
                    <Field label="Location">
                      <input className={inputCls} value={cfg.setup_details?.location || ''} onChange={(e) => set({ setup_details: { ...(cfg.setup_details || {}), location: e.target.value } })} placeholder="e.g. Chennai, Pune" />
                    </Field>
                    <Field label="Work mode">
                      <select className={inputCls} value={cfg.setup_details?.work_mode || ''} onChange={(e) => set({ setup_details: { ...(cfg.setup_details || {}), work_mode: normalizeWorkMode(e.target.value) } })}>
                        <option value="">Not set</option>
                        {['onsite', 'hybrid', 'remote', 'field'].map((mode) => <option key={mode} value={mode}>{mode}</option>)}
                      </select>
                    </Field>
                    {(isAI || isHybrid) ? (
                      <Field label="Question count"
                        hint={(cfg.question_count_mode || 'fixed') === 'unrestricted'
                          ? 'The AI asks as many as needed to cover the role, bounded by the duration.'
                          : 'Wraps up around this many main questions (follow-ups are extra).'}>
                        <div className="space-y-2">
                          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-1 text-sm dark:border-gray-700 dark:bg-gray-800">
                            {[['fixed', 'Fixed count'], ['unrestricted', 'Unrestricted']].map(([v, label]) => (
                              <button key={v} type="button" onClick={() => set({ question_count_mode: v })}
                                className={`rounded-md px-3 py-1 font-medium transition ${(cfg.question_count_mode || 'fixed') === v
                                  ? 'bg-primary-600 text-white shadow'
                                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                                {label}
                              </button>
                            ))}
                          </div>
                          {(cfg.question_count_mode || 'fixed') !== 'unrestricted' && (
                            <input type="number" min="1" className={inputCls} value={cfg.total_questions}
                              onChange={(e) => set({ total_questions: Number(e.target.value) })} />
                          )}
                        </div>
                      </Field>
                    ) : (
                      <Field label="Total questions">
                        <input type="number" min="1" className={inputCls} value={cfg.total_questions} onChange={(e) => set({ total_questions: Number(e.target.value) })} />
                      </Field>
                    )}
                    <Field label="Duration (minutes)" hint="Pacing is automatic — it wraps up near this time.">
                      <input type="number" min="1" className={inputCls} value={cfg.duration_minutes} onChange={(e) => set({ duration_minutes: Number(e.target.value) })} />
                    </Field>
                  </div>
                </Section>

                <Section title="Interview mode" desc="How the questions are generated.">
                  <div className="grid gap-2.5 sm:grid-cols-3">
                    {MODES.map((m) => (
                      <button key={m.value} type="button"
                        onClick={() => set(m.value === 'recruiter' ? { mode: m.value, question_count_mode: 'fixed' } : { mode: m.value })}
                        className={cardBtn(cfg.mode === m.value)}>
                        <span className="block font-semibold text-gray-800 dark:text-gray-100">{m.label}</span>
                        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{m.hint}</span>
                      </button>
                    ))}
                  </div>
                  {modeMeta.example && <p className="mt-2.5 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">{modeMeta.example}</p>}
                </Section>

                {(isAI || isHybrid) && (
                  <Section title="Priority topics"
                    desc="High-priority focus areas the AI asks first — each replaces the generic question of whichever category it best fits. If interview time remains after covering them, it continues with the job description skills.">
                    <PriorityTopicsEditor
                      topics={cfg.setup_details?.priority_topics || []}
                      onChange={(next) => set({ setup_details: { ...(cfg.setup_details || {}), priority_topics: next } })}
                      onValidate={(topics) => validateTopics(companyId, { topics, sessionId: scope === 'job' ? jobId : (cfg.session_id || '') })}
                      example={(cfg.setup_details?.skills || []).slice(0, 3).join(', ')}
                    />
                  </Section>
                )}

                {(isAI || isHybrid) && (
                  <Section title="AI question categories"
                    desc={isHybrid
                      ? `The AI’s share (${aiTarget} of ${total} — the rest are your questions).`
                      : 'Which areas the AI covers, and how many questions each gets.'}
                    right={(isAI && aiMix === 'auto') ? null : <Pill ok={catMatch}>{categorySum(cfg)} / {aiTarget}</Pill>}>

                    {isAI && (
                      <div className="mb-4 inline-flex rounded-lg border border-gray-200 bg-gray-100 p-1 text-sm dark:border-gray-700 dark:bg-gray-800">
                        {[['auto', 'Let AI decide'], ['custom', 'Choose categories']].map(([v, label]) => (
                          <button key={v} type="button" onClick={() => setAiMixMode(v)}
                            className={`rounded-md px-3 py-1 font-medium transition ${aiMix === v
                              ? 'bg-primary-600 text-white shadow'
                              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    )}

                    {isAI && aiMix === 'auto' ? (
                      <p className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                        The AI plans the best {total} questions across all relevant areas (resume, JD, technical,
                        problem-solving, behavioural…), adapting to each candidate. This is the classic AI interview.
                      </p>
                    ) : (
                      <>
                        <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 dark:divide-gray-800 dark:border-gray-700">
                          {(cfg.categories || []).map((c, i) => (
                            <div key={i} className="flex flex-wrap items-center gap-2 p-2.5">
                              <select className="min-w-[11rem] flex-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" value={c.category_name} onChange={(e) => setCategory(i, { category_name: e.target.value })}>
                                {catMeta.map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}
                              </select>
                              {catBadges(c.category_name)}
                              <div className="ml-auto flex items-center rounded-lg border border-gray-300 dark:border-gray-600">
                                <button type="button" onClick={() => bumpCategory(i, -1)} className="px-2.5 py-1 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100">−</button>
                                <span className="w-8 text-center text-sm font-medium text-gray-800 dark:text-gray-100">{c.question_count}</span>
                                <button type="button" onClick={() => bumpCategory(i, 1)} className="px-2.5 py-1 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100">+</button>
                              </div>
                              <button type="button" onClick={() => removeCategory(i)} className="px-1.5 text-rose-400 hover:text-rose-600">✕</button>
                            </div>
                          ))}
                          {(cfg.categories || []).length === 0 && (
                            <p className="p-3 text-sm text-gray-400 dark:text-gray-500">No categories yet — add some, or switch to “Let AI decide”.</p>
                          )}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button type="button" onClick={addCategory} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800">+ Add category</button>
                          <button type="button" onClick={addNewCategoryType} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800">+ New category type</button>
                        </div>
                      </>
                    )}
                  </Section>
                )}

                {(isRecruiter || isHybrid) && (
                  <Section title="Your questions" desc="The AI asks each in full, conversationally — never skipped or reworded."
                    right={<Pill ok={isRecruiter ? recruiterCount(cfg) === total : recruiterCount(cfg) >= 1}>{recruiterCount(cfg)}{isRecruiter ? ` / ${total}` : ''}</Pill>}>
                    <div className="space-y-3">
                      {(cfg.custom_questions || []).map((q, i) => (
                        <div key={i} className="rounded-xl border border-gray-200 bg-gray-50/50 p-3 dark:border-gray-700 dark:bg-gray-800/40">
                          <div className="mb-2 flex items-center justify-between">
                            <span className="grid h-5 w-5 place-items-center rounded-full bg-sky-100 text-[11px] font-bold text-sky-700 dark:bg-sky-900/40 dark:text-sky-400">{i + 1}</span>
                            <div className="flex items-center gap-1.5">
                              <button type="button" onClick={() => moveQuestion(i, -1)} disabled={i === 0}
                                title="Move up" aria-label="Move question up"
                                className="text-gray-400 transition hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:text-gray-200">↑</button>
                              <button type="button" onClick={() => moveQuestion(i, 1)} disabled={i === (cfg.custom_questions || []).length - 1}
                                title="Move down" aria-label="Move question down"
                                className="text-gray-400 transition hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:text-gray-200">↓</button>
                              <button type="button" onClick={() => removeQuestion(i)} title="Remove" aria-label="Remove question"
                                className="ml-1 text-rose-400 hover:text-rose-600">✕</button>
                            </div>
                          </div>
                          <textarea className={`${inputCls} mb-2`} rows={2} value={q.question_text}
                            onChange={(e) => setQuestion(i, { question_text: e.target.value })} placeholder="Type the exact question to ask…" />
                          <div className="grid gap-2 sm:grid-cols-3">
                            <select className={inputCls} value={q.category} onChange={(e) => setQuestion(i, { category: e.target.value })}>
                              {catMeta.map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}
                            </select>
                            <input className={inputCls} value={(q.expected_skills || []).join(', ')}
                              onChange={(e) => setQuestion(i, { expected_skills: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                              placeholder="Expected skills (comma-separated)" />
                            <input type="number" min="0" className={inputCls} value={q.weightage}
                              onChange={(e) => setQuestion(i, { weightage: Number(e.target.value) })} placeholder="Weight" />
                          </div>
                        </div>
                      ))}
                      {(cfg.custom_questions || []).length === 0 && <p className="text-sm text-gray-400 dark:text-gray-500">No questions yet — add the ones the AI must ask.</p>}
                    </div>
                    <button type="button" onClick={addQuestion} className="mt-3 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800">+ Add question</button>
                  </Section>
                )}

                {(isRecruiter || isHybrid) && (
                  <Section title="Your question order"
                    desc="The order the AI asks YOUR own questions in.">
                    <select className={inputCls} value={cfg.recruiter_question_order || 'created'}
                      onChange={(e) => set({ recruiter_question_order: e.target.value })}>
                      {RECRUITER_ORDERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                      {(RECRUITER_ORDERS.find((o) => o.value === (cfg.recruiter_question_order || 'created')) || {}).hint}
                    </p>
                  </Section>
                )}

                {isHybrid && (
                  <Section title="Mix with AI questions" desc="Where your questions sit relative to the AI’s.">
                    <select className={inputCls} value={cfg.placement_strategy} onChange={(e) => set({ placement_strategy: e.target.value })}>
                      {PLACEMENT_STRATEGIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </Section>
                )}

                <Section title="If the candidate goes quiet"
                  desc="How long to wait in silence before the AI checks in with “are you still there?”, how many times to check in, and how long after the last check-in to end the interview.">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Field label="Check in after (seconds)" hint="Silence before each “are you there?”.">
                      <input type="number" min="0" className={inputCls} value={cfg.presence_nudge_seconds ?? 60}
                        onChange={(e) => set({ presence_nudge_seconds: Number(e.target.value) })} />
                    </Field>
                    <Field label="Number of check-ins" hint="How many times before ending.">
                      <input type="number" min="0" className={inputCls} value={cfg.presence_nudge_count ?? 2}
                        onChange={(e) => set({ presence_nudge_count: Number(e.target.value) })} />
                    </Field>
                    <Field label="End after last check (seconds)" hint="Final wait, then auto-end.">
                      <input type="number" min="0" className={inputCls} value={cfg.presence_end_after_last_nudge_seconds ?? 60}
                        onChange={(e) => set({ presence_end_after_last_nudge_seconds: Number(e.target.value) })} />
                    </Field>
                  </div>
                  <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                    Total silence before the interview auto-ends ≈ {((Number(cfg.presence_nudge_count ?? 2) * Number(cfg.presence_nudge_seconds ?? 60)) + Number(cfg.presence_end_after_last_nudge_seconds ?? 60))} seconds.
                    The overall <strong>Duration</strong> above is a separate hard time limit.
                  </p>
                  <div className="mt-4 max-w-xs">
                    <Field label="Allow “keep going” on End if more than (seconds) left"
                      hint="When the candidate taps End, offer to continue only if at least this much time remains; below it, End is the only option.">
                      <input type="number" min="0" className={inputCls} value={cfg.rejoin_min_seconds ?? 60}
                        onChange={(e) => set({ rejoin_min_seconds: Number(e.target.value) })} />
                    </Field>
                  </div>
                </Section>

                <p className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400">
                  Scoring, thresholds and follow-up depth are inherited from the <strong>Company policy</strong> tab.
                </p>
              </>
            )}

            {scope === 'company' && (
              <>
                <Section title="Company details" desc="The AI uses these to answer a candidate’s questions about the company (where it’s based, what it does). Leave blank and the AI politely defers to the hiring team.">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Company name">
                      <input className={inputCls} value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. TalentGPT" />
                    </Field>
                    <Field label="Location / address">
                      <input className={inputCls} value={profile.address} onChange={(e) => setProfile((p) => ({ ...p, address: e.target.value }))} placeholder="e.g. Coimbatore, India" />
                    </Field>
                    <label className="block text-sm sm:col-span-2">
                      <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">About the company</span>
                      <textarea className={inputCls} rows={3} value={profile.description}
                        onChange={(e) => setProfile((p) => ({ ...p, description: e.target.value }))}
                        placeholder="What the company does, mission, culture — anything the AI can share with candidates." />
                    </label>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <button type="button" onClick={saveProfile} disabled={profileBusy}
                      className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50">
                      {profileBusy ? 'Saving…' : 'Save company details'}
                    </button>
                    {profileMsg && <span className={`text-sm ${profileMsg.type === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{profileMsg.text}</span>}
                  </div>
                </Section>

                <Section title="Company policy Q&A" desc="Policy documents are used only when candidates ask about benefits, leave, company process, culture, or other company policy topics.">
                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
                    <div className="space-y-3">
                      <Field label="Document title">
                        <input className={inputCls} value={policyDraft.title}
                          onChange={(e) => setPolicyDraft((p) => ({ ...p, title: e.target.value }))}
                          placeholder="Leave policy, benefits guide, interview FAQ" />
                      </Field>
                      <label className="block text-sm">
                        <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">Policy text</span>
                        <textarea className={inputCls} rows={7} value={policyDraft.raw_text}
                          onChange={(e) => setPolicyDraft((p) => ({ ...p, raw_text: e.target.value }))}
                          placeholder="Paste approved company policy text here. The AI will answer only from indexed policy chunks." />
                      </label>
                      <div className="flex flex-wrap items-center gap-3">
                        <button type="button" onClick={savePolicyDocument} disabled={policyBusy}
                          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50">
                          {policyBusy ? 'Indexing…' : 'Index policy document'}
                        </button>
                        {policyMsg && <span className={`text-sm ${policyMsg.type === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{policyMsg.text}</span>}
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Indexed documents</h3>
                        <Pill ok={Boolean(policyStatus?.has_policy)}>{policyStatus?.document_count || policyDocs.length || 0}</Pill>
                      </div>
                      <div className="mt-3 space-y-2">
                        {policyDocs.length === 0 ? (
                          <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">No policy document indexed. Candidate company-policy questions will use the fallback answer.</p>
                        ) : policyDocs.map((doc) => (
                          <div key={doc.id} className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{doc.title || `Document ${doc.id}`}</p>
                                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{doc.chunk_count || 0} chunks · {doc.status || 'ready'}</p>
                              </div>
                              <button type="button" onClick={() => removePolicyDocument(doc)} disabled={policyBusy}
                                className="shrink-0 rounded-md border border-rose-200 px-2 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-900/20">
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Section>

                <Section title="Scoring weights" desc="Each criterion’s share of the 0–100 score. Shared across every job."
                  right={<Pill ok={scoringSum(cfg) === 100}>{scoringSum(cfg)}% / 100%</Pill>}>
                  <div className="space-y-2">
                    {(cfg.scoring_config.criteria || []).map((c, i) => (
                      <div key={c.key || i} className="flex items-center gap-2">
                        <input className={`${inputCls} flex-1`} value={c.label || ''} onChange={(e) => setCriterion(i, { label: e.target.value })} />
                        <input type="number" min="0" max="100" className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                          value={c.weight} onChange={(e) => setCriterion(i, { weight: Number(e.target.value) })} />
                        <span className="text-sm text-gray-400 dark:text-gray-500">%</span>
                      </div>
                    ))}
                  </div>
                </Section>

                <Section title="Selection thresholds" desc="Overall-score (0–100) cutoffs for the recommendation.">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Recommended ≥">
                      <input type="number" min="0" max="100" className={inputCls} value={cfg.threshold_config.select ?? 70}
                        onChange={(e) => set({ threshold_config: { ...cfg.threshold_config, select: Number(e.target.value) } })} />
                    </Field>
                    <Field label="Review required ≥" hint="Below this → Rejected">
                      <input type="number" min="0" max="100" className={inputCls} value={cfg.threshold_config.maybe ?? 50}
                        onChange={(e) => set({ threshold_config: { ...cfg.threshold_config, maybe: Number(e.target.value) } })} />
                    </Field>
                  </div>
                </Section>

                <Section title="Follow-up depth" desc="How much the AI probes each answer (shared default).">
                  <div className="grid gap-2.5 sm:grid-cols-3">
                    {FOLLOWUP_MODES.map((f) => (
                      <button key={f.value} type="button" onClick={() => set({ followup_mode: f.value })} className={cardBtn(cfg.followup_mode === f.value)}>
                        <span className="block font-semibold text-gray-800 dark:text-gray-100">{f.label}</span>
                        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{f.hint}</span>
                      </button>
                    ))}
                  </div>
                </Section>

                {!companyOnly && <Section title="Fallback defaults" desc="Used for jobs that don’t set their own total / duration.">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Default total questions">
                      <input type="number" min="1" className={inputCls} value={cfg.total_questions} onChange={(e) => set({ total_questions: Number(e.target.value) })} />
                    </Field>
                    <Field label="Default duration (minutes)">
                      <input type="number" min="1" className={inputCls} value={cfg.duration_minutes} onChange={(e) => set({ duration_minutes: Number(e.target.value) })} />
                    </Field>
                  </div>
                </Section>}
              </>
            )}
          </div>

          {/* ════════ RIGHT: sticky preview + save ════════ */}
          <aside className="space-y-4 lg:sticky lg:top-6">
            {!companyOnly && <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
              <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">Blueprint preview</h2>
              {!preview ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">Adjust the configuration to preview…</p>
              ) : (
                <div className="space-y-4 text-sm">
                  <div className="flex flex-wrap gap-2">
                    <span className={`${chipBase} bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300`}>{(MODES.find((m) => m.value === preview.mode) || {}).label || preview.mode}</span>
                    {preview.role_level && <span className={`${chipBase} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`}>{preview.role_level}</span>}
                    {preview.department && <span className={`${chipBase} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`}>{preview.department}</span>}
                    {preview.location && <span className={`${chipBase} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`}>{preview.location}</span>}
                    {preview.work_mode && <span className={`${chipBase} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`}>{preview.work_mode}</span>}
                    <span className={`${chipBase} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`}>{preview.total_questions} questions</span>
                    <span className={`${chipBase} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`}>{preview.duration_minutes} min</span>
                    <span className={`${chipBase} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`}>follow-ups: {preview.followup_mode}</span>
                    {preview.mode !== 'ai' && preview.recruiter_question_order && (
                      <span className={`${chipBase} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`}>order: {(RECRUITER_ORDERS.find((o) => o.value === preview.recruiter_question_order) || {}).label || preview.recruiter_question_order}</span>
                    )}
                    {preview.silence && (
                      <span className={`${chipBase} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`}
                        title="Silence before the AI checks in × check-ins + final wait">
                        silence: {preview.silence.nudge_seconds}s × {preview.silence.nudge_count} + {preview.silence.end_after_last_nudge_seconds}s
                      </span>
                    )}
                  </div>

                  {preview.mode !== 'recruiter' && (preview.priority_topics || []).length > 0 && (
                    <div>
                      <div className="mb-1.5 font-medium text-gray-700 dark:text-gray-300">Priority topics (asked first)</div>
                      <div className="flex flex-wrap gap-1.5">
                        {preview.priority_topics.map((t) => (
                          <span key={t} className={`${chipBase} bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300`}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {preview.mode === 'recruiter' ? (
                    <div>
                      <div className="mb-1.5 font-medium text-gray-700 dark:text-gray-300">AI’s role</div>
                      <p className="text-gray-500 dark:text-gray-400">
                        The AI asks only your questions, in your order. It just handles the intro, follow-ups, your wrap-up Q&amp;A, and the closing.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-1.5 font-medium text-gray-700 dark:text-gray-300">AI question mix</div>
                      {Object.keys(preview.category_distribution || {}).length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400">AI chooses automatically across all relevant areas.</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(preview.category_distribution).map(([cat, n]) => (
                            <span key={cat} className={`${chipBase} bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300`}>{labelFor(cat, catMeta)} × {n}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {(preview.custom_questions || []).length > 0 && (
                    <div>
                      <div className="mb-1.5 font-medium text-gray-700 dark:text-gray-300">Your questions ({preview.custom_questions.length})</div>
                      <ol className="ml-4 list-decimal space-y-0.5 text-gray-600 dark:text-gray-400">
                        {preview.custom_questions.slice(0, 5).map((q, i) => <li key={i} className="truncate">{q.text}</li>)}
                        {preview.custom_questions.length > 5 && <li className="text-gray-400 dark:text-gray-500">+ {preview.custom_questions.length - 5} more…</li>}
                      </ol>
                    </div>
                  )}

                  <div>
                    <div className="mb-1.5 font-medium text-gray-700 dark:text-gray-300">Scoring (from company policy)</div>
                    <div className="space-y-1">
                      {(preview.scoring_weights || []).map((w) => (
                        <div key={w.key || w.label} className="flex items-center gap-2">
                          <span className="w-36 shrink-0 truncate text-xs text-gray-600 dark:text-gray-400">{w.label}</span>
                          <div className="h-2 flex-1 overflow-hidden rounded bg-gray-100 dark:bg-gray-800">
                            <div className="h-full rounded bg-primary-400" style={{ width: `${Math.min(100, Number(w.weight) || 0)}%` }} />
                          </div>
                          <span className="w-9 shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">{w.weight}%</span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                      Recommend ≥ {(preview.thresholds || {}).select} · Review ≥ {(preview.thresholds || {}).maybe} (/100)
                    </p>
                  </div>

                  <details className="rounded-lg bg-gray-900 p-3 text-gray-100 dark:bg-gray-950">
                    <summary className="cursor-pointer text-xs text-gray-300">Raw blueprint (JSON)</summary>
                    <pre className="mt-2 max-h-56 overflow-auto text-xs leading-relaxed">{JSON.stringify(preview, null, 2)}</pre>
                  </details>
                </div>
              )}
            </div>}

            <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
              {errors.length > 0 ? (
                <ul className="mb-3 space-y-1 text-sm text-rose-600 dark:text-rose-400">
                  {errors.map((e, i) => <li key={i} className="flex gap-1.5"><span>•</span><span>{e}</span></li>)}
                </ul>
              ) : (
                <p className="mb-3 text-sm font-medium text-emerald-600 dark:text-emerald-400">✓ Ready to save</p>
              )}
              <button type="button" onClick={save} disabled={busy || errors.length > 0}
                className="w-full rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50">
                {busy ? 'Saving…' : (scope === 'company' ? 'Save company policy' : (selectedId ? 'Update interview' : 'Save interview'))}
              </button>
              {msg && <p className={`mt-2 text-sm ${msg.type === 'success' ? 'text-emerald-600 dark:text-emerald-400' : msg.type === 'info' ? 'text-sky-600 dark:text-sky-400' : 'text-rose-600 dark:text-rose-400'}`}>{msg.text}</p>}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default InterviewConfigurationPage;
